import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const ROL_COLORS = { admin: '#818cf8', supervisor: '#34d399', cobrador: '#38bdf8', almacenista_cobrador: '#fb923c' };
const ROL_DESC = {
  cobrador:             'Registra cobros y gestiona rutas asignadas',
  almacenista_cobrador: 'Cobrador que además gestiona inventario/almacén',
  supervisor:           'Ve reportes y supervisa cobradores',
  admin:                'Acceso completo a todas las funciones',
};
const EMPTY = { nombre: '', email: '', password: '', rol: 'cobrador' };

export default function Usuarios() {
  const { usuario } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editRolOriginal, setEditRolOriginal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('');

  const cargar = useCallback(() => {
    api.get('/usuarios').then(r => setUsuarios(r.data)).catch(() => {});
    // Solo cargar info del plan si no es superadmin
    if (!usuario?.is_superadmin) {
      api.get('/config/plan').then(r => setPlanInfo(r.data)).catch(() => {});
    }
  }, [usuario]);

  useEffect(() => { cargar(); }, [cargar]);

  const sillasUsadas  = planInfo?.sillas_usadas  ?? null;
  const sillasMax     = planInfo?.sillas_max      ?? null;
  const sillasLibres  = planInfo?.sillas_libres   ?? null;
  const pct           = planInfo?.porcentaje_uso  ?? 0;
  const alLimite      = sillasMax !== null && sillasUsadas >= sillasMax;
  const barColor      = pct >= 90 ? '#f87171' : pct >= 70 ? '#fbbf24' : '#34d399';

  const usuariosFiltrados = usuarios.filter(u => {
    const q = filtro.toLowerCase();
    return !q || u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.rol.includes(q);
  });

  const abrirNuevo = () => {
    if (alLimite) {
      toast.error(`Límite de ${sillasMax} silla(s) alcanzado. Desactiva un usuario o solicita un upgrade.`);
      return;
    }
    setForm(EMPTY); setEditId(null); setEditRolOriginal(null); setModal(true);
  };

  const abrirEditar = (u) => {
    setForm({ nombre: u.nombre, email: u.email, password: '', rol: u.rol });
    setEditId(u.id);
    setEditRolOriginal(u.rol);
    setModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) {
        const payload = { nombre: form.nombre, email: form.email, rol: form.rol };
        if (form.password) payload.password = form.password;
        await api.put(`/usuarios/${editId}`, payload);
        toast.success('Usuario actualizado');
      } else {
        await api.post('/usuarios', form);
        toast.success('Usuario creado');
      }
      setModal(false); setForm(EMPTY); setEditId(null);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const desactivar = async (id) => {
    if (!window.confirm('¿Desactivar este usuario? Perderá acceso al sistema.')) return;
    try {
      await api.delete(`/usuarios/${id}`);
      toast.success('Usuario desactivado');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    }
  };

  const reactivar = async (id) => {
    if (alLimite) {
      toast.error(`No puedes reactivar: límite de ${sillasMax} silla(s) alcanzado.`);
      return;
    }
    try {
      await api.put(`/usuarios/${id}`, { activo: 1 });
      toast.success('Usuario reactivado');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--text-1)', fontSize: 22, fontWeight: 700 }}>Usuarios</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-3)', fontSize: 13 }}>
            Gestiona los usuarios con acceso a Prestam.io
          </p>
        </div>
        <button
          onClick={abrirNuevo}
          disabled={alLimite}
          style={{
            padding: '10px 20px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 14, cursor: alLimite ? 'not-allowed' : 'pointer',
            background: alLimite ? '#334155' : '#6366f1', color: alLimite ? '#64748b' : '#fff',
          }}
        >
          + Nuevo Usuario
        </button>
      </div>

      {/* Barra de sillas */}
      {planInfo?.configurado && sillasMax !== null && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '16px 20px', marginBottom: 20, border: `1px solid ${alLimite ? '#f8717140' : '#334155'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>💺 Sillas del plan <span style={{ color: planInfo.suscripcion?.plan_color, fontSize: 13 }}>{planInfo.suscripcion?.plan_nombre}</span></span>
            </div>
            <span style={{ fontSize: 13, color: alLimite ? '#f87171' : '#64748b', fontWeight: alLimite ? 700 : 400 }}>
              {sillasUsadas} / {sillasMax} usadas · <span style={{ color: barColor }}>{sillasLibres} libres</span>
            </span>
          </div>
          <div style={{ background: 'var(--bg-app)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(pct, 100)}%`, background: barColor, height: '100%', borderRadius: 6, transition: 'width 0.4s' }} />
          </div>
          {alLimite && (
            <p style={{ margin: '8px 0 0', color: '#f87171', fontSize: 12 }}>
              ⚠ Límite alcanzado. Desactiva un usuario existente o solicita un upgrade de plan para agregar más usuarios.
            </p>
          )}
          {!alLimite && pct >= 70 && (
            <p style={{ margin: '8px 0 0', color: '#fbbf24', fontSize: 12 }}>
              Estás al {pct}% de capacidad. Quedan {sillasLibres} silla(s) disponible(s).
            </p>
          )}
        </div>
      )}

      {/* Roles info */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        {Object.entries(ROL_DESC).map(([rol, desc]) => (
          <div key={rol} style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '12px 14px', border: `1px solid ${ROL_COLORS[rol]}30` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: ROL_COLORS[rol], flexShrink: 0 }} />
              <span style={{ fontWeight: 700, color: ROL_COLORS[rol], fontSize: 13, textTransform: 'capitalize' }}>{rol}</span>
              <span style={{ background: 'var(--bg-app)', color: 'var(--text-3)', fontSize: 11, padding: '1px 7px', borderRadius: 8, marginLeft: 'auto' }}>
                {usuarios.filter(u => u.rol === rol && u.activo).length} activos
              </span>
            </div>
            <p style={{ margin: 0, color: 'var(--text-3)', fontSize: 12 }}>{desc}</p>
          </div>
        ))}
      </div>

      {/* Filtro */}
      <input
        placeholder="Buscar por nombre, email o rol..."
        value={filtro}
        onChange={e => setFiltro(e.target.value)}
        style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-1s)', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
      />

      {/* Lista */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {usuariosFiltrados.map(u => (
          <div key={u.id} style={{
            background: 'var(--bg-card)', borderRadius: 12, padding: '14px 18px',
            border: `1px solid ${u.activo ? '#334155' : '#1e293b'}`,
            display: 'flex', alignItems: 'center', gap: 14,
            opacity: u.activo ? 1 : 0.55,
          }}>
            {/* Avatar */}
            <div style={{
              width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
              background: ROL_COLORS[u.rol] + '22',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, color: ROL_COLORS[u.rol],
            }}>
              {u.nombre.charAt(0).toUpperCase()}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: 'var(--text-1)', fontSize: 14 }}>{u.nombre}</span>
                <span style={{ padding: '1px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: ROL_COLORS[u.rol] + '22', color: ROL_COLORS[u.rol], textTransform: 'capitalize' }}>{u.rol}</span>
                {!u.activo && <span style={{ padding: '1px 9px', borderRadius: 20, fontSize: 11, background: 'var(--bg-hover)', color: 'var(--text-3)' }}>Inactivo</span>}
              </div>
              <div style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 2 }}>{u.email}</div>
            </div>

            {/* Acciones */}
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {u.activo && (
                <>
                  <button onClick={() => abrirEditar(u)} style={btnStyle('#334155', '#94a3b8')}>Editar</button>
                  {u.id !== usuario?.id && (
                    <button onClick={() => desactivar(u.id)} style={btnStyle('#7f1d1d30', '#f87171')}>Desactivar</button>
                  )}
                </>
              )}
              {!u.activo && (
                <button
                  onClick={() => reactivar(u.id)}
                  disabled={alLimite}
                  style={btnStyle(alLimite ? '#1e293b' : '#14532d30', alLimite ? '#334155' : '#34d399')}
                  title={alLimite ? 'Sin sillas disponibles' : 'Reactivar usuario'}
                >
                  Reactivar
                </button>
              )}
            </div>
          </div>
        ))}
        {!usuariosFiltrados.length && (
          <div style={{ textAlign: 'center', color: 'var(--text-4)', padding: 40 }}>No hay usuarios</div>
        )}
      </div>

      {/* Modal crear/editar */}
      {modal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <h3 style={{ margin: 0, color: 'var(--text-1)', fontSize: 17 }}>{editId ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>

            {/* Alerta sillas si está cerca del límite al crear */}
            {!editId && sillasMax !== null && (
              <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: 'var(--text-3)', display: 'flex', justifyContent: 'space-between' }}>
                <span>Sillas disponibles</span>
                <span style={{ color: barColor, fontWeight: 700 }}>{sillasLibres} de {sillasMax}</span>
              </div>
            )}

            <form onSubmit={guardar}>
              <Field label="Nombre completo">
                <input style={inputStyle} value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required placeholder="Juan García" />
              </Field>
              <Field label="Email">
                <input style={inputStyle} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="juan@empresa.com" />
              </Field>
              <Field label={editId ? 'Nueva contraseña (vacío = sin cambio)' : 'Contraseña'}>
                <input style={inputStyle} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editId} placeholder="Mínimo 6 caracteres" />
              </Field>
              <Field label="Rol">
                {editRolOriginal === 'admin' ? (
                  <>
                    <select style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} value="admin" disabled>
                      <option value="admin">Administrador — acceso completo</option>
                    </select>
                    <p style={{ margin: '5px 0 0', color: 'var(--text-4)', fontSize: 11 }}>
                      El rol de un administrador no se puede cambiar desde aquí.
                    </p>
                  </>
                ) : (
                  <select style={inputStyle} value={form.rol} onChange={e => setForm({ ...form, rol: e.target.value })}>
                    <option value="cobrador">Cobrador — registra cobros y sigue rutas</option>
                    <option value="almacenista_cobrador">Cobrador/Almacenista — cobros + inventario</option>
                    <option value="supervisor">Supervisor — ve reportes y supervisa cobradores</option>
                    {!editId && <option value="admin">Administrador — acceso completo</option>}
                  </select>
                )}
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
                <button type="button" onClick={() => setModal(false)} style={btnStyle('#334155', '#94a3b8', '10px 18px')}>Cancelar</button>
                <button type="submit" disabled={loading} style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                  {loading ? 'Guardando...' : editId ? 'Actualizar' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

const btnStyle = (bg, color, padding = '6px 12px') => ({
  padding, borderRadius: 8, border: 'none', background: bg,
  color, cursor: 'pointer', fontSize: 12, fontWeight: 500,
});

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20,
};

const modalStyle = {
  background: 'var(--bg-card)', borderRadius: 16, padding: 28,
  width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
  border: '1px solid var(--border)',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 10,
  border: '1px solid var(--border)', background: 'var(--bg-app)',
  color: 'var(--text-1)', fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', color: 'var(--text-3)', fontSize: 12, marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}
